import axios from "axios";

// Store CSRF token in memory (not localStorage)
let csrfToken = null;

// Track if a token refresh is in progress to prevent multiple simultaneous requests
let isRefreshing = false;
let refreshPromise = null;

// Track token age
let tokenCreationTime = null;
const TOKEN_MAX_AGE = 55 * 60 * 1000; // 55 minutes in milliseconds

// Function to get the current CSRF token
export const getCsrfToken = () => csrfToken;

// Function to set the CSRF token
export const setCsrfToken = (token) => {
  if (token) {
    csrfToken = token;
    // Update default axios header
    axios.defaults.headers.common["X-CSRF-TOKEN"] = token;

    // Store token creation time for expiration tracking
    tokenCreationTime = Date.now();
  }
};

// Function to check if token needs refreshing
export const isTokenExpired = () => {
  if (!csrfToken || !tokenCreationTime) return true;

  const tokenAge = Date.now() - tokenCreationTime;
  return tokenAge > TOKEN_MAX_AGE;
};

// Function to refresh the CSRF token
export const refreshCsrfToken = async () => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = axios
    .get("/api/auth/csrf-token", {
      withCredentials: true,
    })
    .then((response) => {
      if (response.data && response.data.csrf_token) {
        setCsrfToken(response.data.csrf_token);
        return response.data.csrf_token;
      }
      throw new Error("Failed to get CSRF token");
    })
    .catch((error) => {
      console.error("Error refreshing CSRF token:", error);
      throw error;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

// Setup axios interceptor for adding CSRF token to requests
export const setupCsrfInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config) => {
      // Only add CSRF token for state-changing methods
      if (
        ["post", "put", "patch", "delete"].includes(config.method) &&
        !config.skipCsrf
      ) {
        // If token is missing or possibly expired, try to refresh
        if (!csrfToken || isTokenExpired()) {
          try {
            await refreshCsrfToken();
          } catch (error) {
            console.error("Could not refresh CSRF token:", error);
          }
        }

        // Set the token in the request header
        config.headers["X-CSRF-TOKEN"] = csrfToken;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Handle CSRF token errors (usually 403 Forbidden with CSRF error)
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If error is CSRF related and we haven't tried to retry yet
      if (
        error.response &&
        error.response.status === 403 &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.includes("CSRF") &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          // Refresh the token and retry
          await refreshCsrfToken();
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
